import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRole, type JobListQuery } from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { useJobs } from '@/features/jobs/use-jobs';
import { JobListingCard } from '@/features/jobs/JobListingCard';
import { useToggleShortlist } from '@/features/shortlist/use-shortlist';
import { useRequestConsent } from '@/features/consent/use-consent';
import { useSubmitApplication } from '@/features/applications/use-applications';
import { PageContainer } from '@/components/PageContainer';
import { ErrorNote, InfoNote } from '@/components/Form';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { Input, SelectInput } from '@/components/ui/Input';
import { SearchIcon } from '@/components/ui/Icons';
import { presentApiError } from '@/lib/form-errors';

const CATEGORIES = [
  'Care',
  'Household',
  'Outdoor',
  'Tutoring',
  'Tech',
  'Creative',
  'Food & hospitality',
  'Retail',
  'Sports & activity',
  'Office & admin',
  'Community',
];

/**
 * Browse, shortlist, ask a guardian, apply. [Teen steps 7-10]
 *
 * All four actions live on one screen because they are one decision: a teen
 * finds a job and either saves it for later or starts the permission it needs.
 */
export function JobsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Partial<JobListQuery>>({ page: 1, pageSize: 12 });
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const jobsQuery = useJobs(filters);
  const shortlist = useToggleShortlist();
  const consent = useRequestConsent();
  const apply = useSubmitApplication();

  const busy = shortlist.isPending || consent.isPending || apply.isPending;
  const isTeen = user?.role === UserRole.TEEN;

  function update(patch: Partial<JobListQuery>): void {
    // Any filter change returns to the first page: staying on page 4 of a
    // narrower result set is how you end up staring at an empty screen.
    setFilters((current) => ({ ...current, ...patch, page: patch.page ?? 1 }));
  }

  async function run(action: () => Promise<unknown>, success: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
    } catch (caught) {
      setError(presentApiError(caught, 'Something went wrong').message);
    }
  }

  const page = jobsQuery.data;

  return (
    <PageContainer>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Badge tone="brand">Opportunities</Badge>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">Find your next gig</h1>
          <p className="mt-2 text-slate-500">
            Age-appropriate jobs from verified providers, with guardian approval built in.
          </p>
        </div>
        {page && (
          <p className="text-sm font-semibold text-slate-500">
            {page.total} open {page.total === 1 ? 'job' : 'jobs'}
          </p>
        )}
      </header>

      {/* Filters */}
      <form
        className="mt-8 rounded-2xl border border-line bg-white p-4 shadow-soft"
        onSubmit={(event) => {
          event.preventDefault();
          update({ q: search.trim() || undefined });
        }}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-3 rounded-xl border border-line px-4 py-2.5">
            <SearchIcon className="size-5 shrink-0 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search jobs"
              placeholder="Search by title or description…"
              className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-slate-400"
            />
          </div>
          <Button type="submit">Search</Button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <SelectInput
            label="Category"
            value={filters.category ?? ''}
            onChange={(event) => update({ category: event.target.value || undefined })}
            optional
          >
            <option value="">All categories</option>
            {CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </SelectInput>

          <Input
            label="City"
            value={filters.city ?? ''}
            onChange={(event) => update({ city: event.target.value || undefined })}
            optional
            placeholder="Austin"
          />

          <SelectInput
            label="Sort by"
            value={`${filters.sortBy ?? 'publishedAt'}:${filters.sortDir ?? 'desc'}`}
            onChange={(event) => {
              const [sortBy, sortDir] = event.target.value.split(':');
              update({
                sortBy: sortBy as JobListQuery['sortBy'],
                sortDir: sortDir as JobListQuery['sortDir'],
              });
            }}
          >
            <option value="publishedAt:desc">Newest first</option>
            <option value="payAmountMinor:desc">Highest paid</option>
            <option value="payAmountMinor:asc">Lowest paid</option>
            <option value="title:asc">Title A–Z</option>
          </SelectInput>
        </div>
      </form>

      <div className="mt-6 space-y-4">
        <ErrorNote message={error} />
        {notice && <InfoNote>{notice}</InfoNote>}
      </div>

      {jobsQuery.isPending && (
        <div className="mt-6 space-y-4" aria-busy="true">
          {[0, 1, 2].map((n) => (
            <Skeleton key={n} className="h-40" />
          ))}
        </div>
      )}

      {jobsQuery.isError && (
        <div className="mt-6 rounded-2xl border border-line bg-white p-8 text-center shadow-soft">
          <p className="text-sm text-slate-500">
            {presentApiError(jobsQuery.error, 'Could not load opportunities').message}
          </p>
          <Button className="mt-5" onClick={() => void jobsQuery.refetch()}>
            Try again
          </Button>
        </div>
      )}

      {page && page.items.length === 0 && (
        <p className="mt-6 rounded-2xl border border-dashed border-line bg-white p-10 text-center text-sm text-slate-500">
          No opportunities match those filters yet. Try widening the search.
        </p>
      )}

      {page && page.items.length > 0 && (
        <>
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {page.items.map((job) => (
              <JobListingCard
                key={job.id}
                job={job}
                role={user?.role ?? ''}
                needsConsent={user?.isMinor ?? false}
                busy={busy}
                onBid={() => void navigate(`/jobs/${job.id}`)}
                onToggleShortlist={() =>
                  void run(
                    () => shortlist.mutateAsync({ jobId: job.id, saved: job.savedByViewer }),
                    job.savedByViewer ? 'Removed from your shortlist.' : 'Saved to your shortlist.',
                  )
                }
                onRequestConsent={() =>
                  void run(
                    () => consent.mutateAsync({ jobId: job.id, consentType: 'APPLY_CONSENT' }),
                    'We have asked your guardian. You can apply once they approve.',
                  )
                }
                onApply={() =>
                  void run(
                    () => apply.mutateAsync({ jobId: job.id }),
                    'Application sent. You will hear back from the job poster.',
                  )
                }
              />
            ))}
          </div>

          {page.totalPages > 1 && (
            <nav
              aria-label="Pagination"
              className="mt-8 flex items-center justify-between gap-4 border-t border-line pt-6"
            >
              <Button
                variant="outline"
                disabled={page.page <= 1}
                onClick={() => update({ page: page.page - 1 })}
              >
                Previous
              </Button>
              <p className="text-sm font-semibold text-slate-500">
                Page {page.page} of {page.totalPages}
              </p>
              <Button
                variant="outline"
                disabled={!page.hasMore}
                onClick={() => update({ page: page.page + 1 })}
              >
                Next
              </Button>
            </nav>
          )}
        </>
      )}

      {!isTeen && (
        <p className="mt-8 text-center text-xs text-slate-400">
          You are browsing as a {user?.role.toLowerCase().replace('_', ' ')}. Only teen accounts can
          shortlist or apply.
        </p>
      )}
    </PageContainer>
  );
}
